/**
 * CommandBar - Universal command interface (⌘K / Ctrl+K)
 * Natural language command and search interface for admins
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import CommandInput from './CommandInput';
import CommandSuggestions from './CommandSuggestions';
import CommandPreview from './CommandPreview';
import CommandResultsPanel from './CommandResultsPanel';
import CommandHistory from './CommandHistory';
import { useCommandBar } from '../hooks/useCommandBar';
import { Pressable } from '../../common/motionSystem';
import './CommandBar.css';

const CommandBar = ({ onNavigate }) => {
  const {
    isOpen,
    input,
    inputType,
    loading,
    error,
    preview,
    results,
    selectedIndex,
    suggestions,
    allSuggestionItems,
    recentCommands,
    quickActions,
    close,
    handleInputChange,
    execute,
    selectSuggestion,
    activateSelectedResult,
    confirmPreview,
    cancelPreview,
    clearResults,
  } = useCommandBar({ onNavigate });

  // Handle quick action click
  const handleQuickAction = (action) => {
    handleInputChange(action.command);
    execute(action.command);
  };

  // Handle suggestion select
  const handleSuggestionSelect = (suggestion, index) => {
    selectSuggestion(suggestion);
  };

  const handleResultNavigate = (destination, payload) => {
    if (onNavigate) onNavigate(destination, payload);
    close();
  };

  const handleSubmit = () => {
    if (results && !preview) {
      activateSelectedResult();
      return;
    }
    execute();
  };

  // Detect platform for shortcut display
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const shortcutKey = isMac ? '⌘K' : 'Ctrl+K';

  return (
    <>
      {/* Persistent mount sentinel — present even when the panel is closed */}
      <div
        data-testid="command-bar-mount"
        className="command-bar-mount"
        aria-hidden="true"
      />

      {/* Command Bar Panel - Slide out from right (⌘K / Ctrl+K) */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="command-bar-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={close}
            />
            <motion.div
              className="command-bar-container"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
              {/* Input Section */}
              <CommandInput
                value={input}
                onChange={handleInputChange}
                onSubmit={handleSubmit}
                inputType={inputType}
                loading={loading}
                shortcutKey={shortcutKey}
              />

              {error && (
                <div className="command-bar-error">
                  <AlertTriangle size={14} />
                  <span>{error}</span>
                </div>
              )}

              {/* Preview Panel (for commands) */}
              {preview && (
                <CommandPreview
                  preview={preview}
                  onConfirm={confirmPreview}
                  onCancel={cancelPreview}
                  loading={loading}
                />
              )}

              {/* Results Panel (for searches) */}
              {results && !preview && (
                <CommandResultsPanel
                  results={results}
                  onClose={clearResults}
                  selectedIndex={selectedIndex}
                  onNavigate={handleResultNavigate}
                />
              )}

              {/* Suggestions (when no preview or results) */}
              {!preview && !results && (
                <>
                  <CommandSuggestions
                    suggestions={suggestions}
                    selectedIndex={selectedIndex}
                    onSelect={handleSuggestionSelect}
                    loading={loading}
                    input={input}
                  />

                  {/* Quick Actions */}
                  {!input && (
                    <div className="command-bar-quick-actions">
                      {quickActions.slice(0, 6).map((action) => {
                        const Icon = action.icon;
                        return (
                          <Pressable
                            as="button"
                            key={action.id}
                            className="command-bar-quick-action"
                            onClick={() => handleQuickAction(action)}
                          >
                            {Icon && <Icon size={14} />}
                            <span>{action.label}</span>
                          </Pressable>
                        );
                      })}
                    </div>
                  )}

                  {/* Recent Commands History */}
                  {!input && recentCommands.length > 0 && (
                    <CommandHistory
                      commands={recentCommands.slice(0, 5)}
                      onSelect={(cmd) => {
                        handleInputChange(cmd.query);
                        execute(cmd.query);
                      }}
                    />
                  )}
                </>
              )}

              {/* Footer with keyboard hints */}
              <div className="command-bar-footer">
                <div className="command-bar-footer-hints">
                  <span className="command-bar-footer-hint">
                    <kbd>↑↓</kbd> Navigate
                  </span>
                  <span className="command-bar-footer-hint">
                    <kbd>Enter</kbd> Select
                  </span>
                  <span className="command-bar-footer-hint">
                    <kbd>Tab</kbd> Autocomplete
                  </span>
                  <span className="command-bar-footer-hint">
                    <kbd>Esc</kbd> Close
                  </span>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default CommandBar;
